import { useState, useEffect } from "react";
import { PageHeader } from "@/components/layout/page-header";
import { CreditCard, Search, Filter } from "lucide-react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { API_URL } from "@/config/api";
import { useAuth } from "@clerk/clerk-react";

interface Transaction {
    id: string;
    reference: string;
    email: string;
    amount: number;
    currency: string;
    status: 'pending' | 'success' | 'failed';
    metadata: {
        donationId?: string;
    } | null;
    type: string;
    createdAt: string;
    updatedAt: string;
}

export default function TransactionsPage() {
    const { getToken } = useAuth();
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState('all');

    useEffect(() => {
        const fetchTransactions = async () => {
            try {
                const token = await getToken();
                const response = await fetch(`${API_URL}/transactions`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    setTransactions(data);
                }
            } catch (error) {
            } finally {
                setIsLoading(false);
            }
        };

        fetchTransactions();
    }, [getToken]);

    const filteredTransactions = transactions.filter(tx =>
        typeFilter === 'all' || tx.type === typeFilter
    );

    return (
        <div className="space-y-8">
            <PageHeader
                title="Transactions"
            />

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-2 bg-card p-2 rounded-lg border border-border">
                    <Filter size={16} className="text-muted-foreground" />
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="border-none bg-transparent focus:ring-0 text-sm text-muted-foreground font-medium"
                    >
                        <option value="all">All Types</option>
                        <option value="donation">Donations</option>
                        <option value="membership">Membership</option>
                        <option value="order">Orders</option>
                    </select>
                </div>

                <button
                    onClick={() => {
                        const csvData = filteredTransactions.map(tx => ({
                            Date: format(new Date(tx.createdAt), "MMM d, yyyy HH:mm"),
                            Reference: tx.reference,
                            Type: tx.type,
                            Email: tx.email,
                            Amount: `${tx.currency} ${tx.amount.toFixed(2)}`,
                            Status: tx.status
                        }));

                        const headers = Object.keys(csvData[0] || {});
                        const csv = [
                            headers.join(','),
                            ...csvData.map(row => headers.map(h => `"${row[h as keyof typeof row]}"`).join(','))
                        ].join('\n');

                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `transactions_export_${format(new Date(), 'yyyy-MM-dd')}.csv`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors text-sm font-medium"
                >
                    Export CSV
                </button>
            </div>

            <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-muted/50 text-muted-foreground font-medium border-b border-border">
                            <tr>
                                <th className="px-6 py-3">Date</th>
                                <th className="px-6 py-3">Reference</th>
                                <th className="px-6 py-3">Type</th>
                                <th className="px-6 py-3">Email</th>
                                <th className="px-6 py-3">Amount</th>
                                <th className="px-6 py-3">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                                        Loading transactions...
                                    </td>
                                </tr>
                            ) : filteredTransactions.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                                        No transactions found.
                                    </td>
                                </tr>
                            ) : (
                                filteredTransactions.map((tx) => (
                                    <tr key={tx.id} className="hover:bg-muted/50 transition-colors">
                                        <td className="px-6 py-4 text-muted-foreground">
                                            {format(new Date(tx.createdAt), "MMM d, yyyy HH:mm")}
                                        </td>
                                        <td className="px-6 py-4 font-mono text-xs text-muted-foreground">
                                            {tx.reference}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                                ${tx.type === 'membership' ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400' :
                                                    tx.type === 'order' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' :
                                                        tx.type === 'donation' ? 'bg-green-500/10 text-green-600 dark:text-green-400' :
                                                            'bg-gray-500/10 text-gray-600 dark:text-gray-400'}`}>
                                                {tx.type || 'Unknown'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-foreground font-medium">
                                            {tx.email}
                                        </td>
                                        <td className="px-6 py-4 text-foreground">
                                            {tx.currency} {tx.amount.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span
                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                          ${tx.status === 'success'
                                                        ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                                                        : tx.status === 'pending'
                                                            ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                                                            : 'bg-red-500/10 text-red-600 dark:text-red-400'
                                                    }`}
                                            >
                                                {tx.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
